/**
 * Renders the "claim your unclaimed items" modal and provides `promptClaim` to open it. Mounted
 * once above the router; a page calls `useRevealClaim().promptClaim(hint)` from a `reveal_pending`
 * error handler. The modal claims the specific blocker the error named — an unclaimed box reveal
 * or a completed battle's winnings — or sells it straight back for its value, and offers an
 * auto-claim toggle so future reveals settle on their own.
 */
import { type ReactNode, useState } from "react";

import { track } from "../lib/analytics.js";
import { lazyOverlay, useLazyOverlay, usePreloadWhenIdle } from "../lib/lazyOverlay.js";
import { type PendingRevealHint, RevealClaimContext } from "../lib/revealClaim.js";
import { useShopper } from "../lib/shopper.js";

/** The modal, its own chunk: fetched once the page is idle for a signed-in shopper. */
const ClaimDialog = lazyOverlay(() =>
  import("./ClaimDialog.js").then((module) => module.ClaimDialog),
);

export function RevealClaimProvider({ children }: { children?: ReactNode }) {
  const [hint, setHint] = useState<PendingRevealHint | null>(null);

  const promptClaim = (next?: PendingRevealHint | null) => {
    setHint(next ?? null);
    if (next) track("claim_prompt_shown", { kind: next.kind });
  };

  usePreloadWhenIdle(ClaimDialog, !!useShopper().user);
  const Dialog = useLazyOverlay(ClaimDialog, hint !== null, () => setHint(null));

  return (
    <RevealClaimContext.Provider value={{ promptClaim }}>
      {children}
      {Dialog && <Dialog hint={hint} onClose={() => setHint(null)} />}
    </RevealClaimContext.Provider>
  );
}
